/**
 * tutorLiveChatViewModel — le chat du live, vu du masteur : le fil d'un canal en jours et en salves, et
 * « … écrit ».
 *
 * Le fil passe par la même mise en forme que le chat de l'élève (`liveChatDays`, la messagerie de
 * `CommonChat.transformMessage`) : dans « Tous », chaque message d'un élève dit son auteur par son
 * visage (`sender`) ; dans le fil d'un élève, l'auteur est l'interlocuteur. Pur.
 */
import { GROUP_CHANNEL } from "../../../../../engine/live/student/liveChatChannels"
import { type LiveChatMessage } from "../../../../../engine/live/student/useLiveChat"
import {
    TUTOR_GROUP_CHANNEL,
    memberFirstname,
    type ApiTutorLiveMember,
} from "../../../adapters/tutor/Live/tutorLiveChatAdapter"
import { type UIConversationDay } from "../../../ui/types/studentConversation"
import { type UILiveChatChannelOption } from "../../../ui/types/studentLive"
import { liveChatDays } from "../../student/Live/studentLiveViewModel"
import { holdsId, type LiveModeReading } from "./tutorLiveStudentsViewModel"

export function tutorChatDays(args: {
    messages: LiveChatMessage[]
    selfId: string | number | null | undefined
    channel: string
    roster: ApiTutorLiveMember[]
    apiUrl: string
    avatarOf: (path: string | null | undefined) => string | undefined
}): UIConversationDay[] {
    const { channel, roster, ...rest } = args
    return liveChatDays({
        ...rest,
        // « Tous » est le seul fil à plusieurs : ses messages disent leur auteur.
        channel: channel === TUTOR_GROUP_CHANNEL ? GROUP_CHANNEL : `student-${channel}`,
        roster: roster.map((member) => ({
            id: member.id ?? undefined,
            is_tutor: member.is_tutor ?? undefined,
            display_name: member.display_name,
            avatar: member.avatar,
        })),
    })
}

/**
 * « Léa écrit… » : l'élève qui écrit, dans tous les fils comme l'ancien chat (`CommonChat.js:1524-1550`,
 * `liveMode.typingUser`). Le masteur lui-même, ou quelqu'un hors du roster : rien.
 */
export function tutorChatTyping(
    typing: { isTyping: boolean; userId: string | number | null | undefined },
    roster: ApiTutorLiveMember[]
): string | undefined {
    if (!typing.isTyping || typing.userId == null) return undefined
    const member = roster.find((item) => String(item.id) === String(typing.userId) && item.is_tutor !== true)
    if (member == null) return undefined
    const firstname = memberFirstname(member)
    return firstname !== "" ? `${firstname} écrit…` : undefined
}

/** Ce que la salle dit des élèves, tel que l'écouteur global `LKEvents` le tient dans `liveMode`. */
export type TutorChatRoomReading = Pick<LiveModeReading, "liveParticipants" | "raisedHands">

/**
 * La présence et la main levée sur la puce de chaque élève, comme l'avatar de l'ancien chat
 * (`tutor/students/LiveAvatarM.js:19-51`, monté par `common/liveMode/LiveChat.js`) :
 *  - le point vert s'il est dans `liveMode.liveParticipants`, gris sinon — jamais pour « Tous »
 *    (l'ancien id 0) ni pour soi (le masteur n'a pas de puce) ;
 *  - la main s'il est dans `liveMode.raisedHands` (les avatars du chat la montrent : `noOutOfApp` faux).
 */
export function tutorChatChannelsInRoom(
    channels: UILiveChatChannelOption[],
    reading: TutorChatRoomReading
): UILiveChatChannelOption[] {
    return channels.map((option) => {
        if (option.student == null) return option
        return {
            ...option,
            student: {
                ...option.student,
                online: holdsId(reading.liveParticipants, option.id),
                handRaised: holdsId(reading.raisedHands, option.id),
            },
        }
    })
}
